import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { isLlmInferenceAvailable } from '../lib/aiCapability';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import RichTextEditor from '../components/RichTextEditor';
import SubjectSelector from '../components/SubjectSelector';
import ConfusedModal from '../components/ConfusedModal';
import { toast } from 'sonner';
import { 
  Mic, 
  Square, 
  Play,
  Pause,
  ChevronLeft,
  Loader2,
  Save,
  Wand2,
  Clock,
  DollarSign,
  AlertCircle,
  Zap,
  FileText,
  Edit3,
  Sparkles,
  Eye,
  HelpCircle
} from 'lucide-react';

const MODELS = [
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Fast & Cheap)', price: '$0.15/1M' },
  { value: 'gpt-4o', label: 'GPT-4o (Balanced)', price: '$2.50/1M' },
  { value: 'gpt-4-turbo', label: 'GPT-4 Turbo (Powerful)', price: '$10/1M' },
  { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo (Budget)', price: '$0.50/1M' },
];

export default function LecturePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  
  // Transcript state
  const [transcript, setTranscript] = useState('');
  const [formattedNotes, setFormattedNotes] = useState('');
  const [formattedNotesHtml, setFormattedNotesHtml] = useState('');
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [lectureTitle, setLectureTitle] = useState('');
  const [subjectId, setSubjectId] = useState(null);
  
  // View state
  const [activeView, setActiveView] = useState('notes'); // 'notes', 'transcript', 'edit'
  
  // UI state
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isFormatting, setIsFormatting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isAutoProcessing, setIsAutoProcessing] = useState(false);
  const [isGeneratingSuggestions, setIsGeneratingSuggestions] = useState(false);
  const [selectedModel, setSelectedModel] = useState(user?.preferred_model || 'gpt-4o-mini');
  const [totalCost, setTotalCost] = useState(0);
  const [currentLecture, setCurrentLecture] = useState(null);
  const [showAutoFormatInfo, setShowAutoFormatInfo] = useState(true);
  const [showConfusedModal, setShowConfusedModal] = useState(false);
  
  // Auto-format is on by default
  const autoFormat = user?.auto_format !== false;
  
  // Lazy transcription state
  const [liveTranscript, setLiveTranscript] = useState('');
  const [isLazyTranscribing, setIsLazyTranscribing] = useState(false);
  const [liveKeyPoints, setLiveKeyPoints] = useState('');
  const [isGeneratingKeyPoints, setIsGeneratingKeyPoints] = useState(false);
  
  // Transcription mode: 'browser' (free) or 'whisper' (paid, more accurate)
  const [transcriptionMode, setTranscriptionMode] = useState(user?.voice_mode || 'browser');
  
  // Track previous transcript for appending multiple recordings
  const [hasPreviousTranscript, setHasPreviousTranscript] = useState(false);
  const previousTranscriptRef = useRef('');
  
  // Keep a ref of the transcript used for formatting (to avoid React state timing issues)
  const transcriptForSaveRef = useRef('');
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const chunkIndexRef = useRef(0);
  const recognitionRef = useRef(null); // Web Speech API
  const pendingChunksRef = useRef([]);
  const transcriptionQueueRef = useRef([]);

  // Load existing lecture if editing
  useEffect(() => {
    if (id) {
      loadLecture();
    }
  }, [id]);

  const loadLecture = async () => {
    try {
      const lecture = await api.getLecture(id);
      setCurrentLecture(lecture);
      setLectureTitle(lecture.title);
      setTranscript(lecture.raw_transcript || '');
      transcriptForSaveRef.current = lecture.raw_transcript || ''; // Keep ref in sync
      previousTranscriptRef.current = lecture.raw_transcript || ''; // For appending new recordings
      setHasPreviousTranscript(!!lecture.raw_transcript);
      setFormattedNotes(lecture.formatted_notes || '');
      setFormattedNotesHtml(lecture.formatted_notes_html || '');
      setAiSuggestions(lecture.ai_suggestions || []);
      setSubjectId(lecture.subject_id || null);
      
      // Switch to notes view if we have formatted notes
      if (lecture.formatted_notes) {
        setActiveView('notes');
      }
    } catch (error) {
      toast.error('Failed to load lecture');
      navigate('/dashboard');
    }
  };

  // Timer effect
  useEffect(() => {
    if (isRecording && !isPaused) {
      timerRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [isRecording, isPaused]);

  // Process a chunk for lazy transcription
  const processChunkForTranscription = async (chunkBlob, index) => {
    if (!hasApiKey) return;
    
    try {
      const result = await api.transcribeChunk(chunkBlob, index);
      if (result.text) {
        // Add to transcription queue in order
        transcriptionQueueRef.current[index] = result.text;
        
        // Rebuild transcript from all received chunks in order
        const orderedText = transcriptionQueueRef.current
          .filter(Boolean)
          .join(' ');
        setLiveTranscript(orderedText);
        setTotalCost(prev => prev + (result.cost || 0));
        
        // Generate live key points for the new chunk (non-blocking)
        generateLiveKeyPoints(result.text);
      }
    } catch (error) {
      console.error(`Chunk ${index} transcription error:`, error);
    }
  };

  // Generate live key points from new transcript content
  const generateLiveKeyPoints = async (newText) => {
    if (!hasApiKey || isGeneratingKeyPoints) return;
    
    setIsGeneratingKeyPoints(true);
    try {
      const result = await api.generateLiveSummary(newText, liveKeyPoints);
      if (result.summary && result.summary !== '...') {
        // Append new key points
        setLiveKeyPoints(prev => {
          if (prev) {
            return prev + '\n' + result.summary;
          }
          return result.summary;
        });
        setTotalCost(prev => prev + (result.cost || 0));
      }
    } catch (error) {
      console.error('Live key points error:', error);
    } finally {
      setIsGeneratingKeyPoints(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      pendingChunksRef.current = [];
      chunkIndexRef.current = 0;
      transcriptionQueueRef.current = [];
      
      // Save existing transcript for appending (if user records again)
      if (transcript) {
        previousTranscriptRef.current = transcript;
        setHasPreviousTranscript(true);
      }
      
      setLiveTranscript('');
      setLiveKeyPoints('');

      // Start transcription based on selected mode
      if (transcriptionMode === 'browser' && browserSpeechSupported) {
        // Use FREE browser speech recognition
        const recognition = setupBrowserSpeechRecognition();
        if (recognition) {
          recognitionRef.current = recognition;
          recognition.start();
          toast.info('Using browser speech recognition (FREE)');
        }
      } else if (transcriptionMode === 'whisper' && !hasApiKey) {
        // Whisper selected but no API key - fall back to browser
        if (browserSpeechSupported) {
          const recognition = setupBrowserSpeechRecognition();
          if (recognition) {
            recognitionRef.current = recognition;
            recognition.start();
            toast.warning('No API key - using free browser transcription');
          }
        }
      }

      // Collect chunks for both full recording and lazy transcription (Whisper mode)
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          
          // Only do Whisper chunk transcription if in whisper mode with API key
          if (transcriptionMode === 'whisper' && hasApiKey) {
            pendingChunksRef.current.push(event.data);
            
            // Every ~10 seconds (10 chunks at 1000ms interval), send for transcription
            if (pendingChunksRef.current.length >= 10) {
              const chunkBlob = new Blob(pendingChunksRef.current, { type: 'audio/webm' });
              const currentIndex = chunkIndexRef.current;
              chunkIndexRef.current++;
              pendingChunksRef.current = [];
              
              setIsLazyTranscribing(true);
              processChunkForTranscription(chunkBlob, currentIndex)
                .finally(() => setIsLazyTranscribing(false));
            }
          }
        }
      };

      mediaRecorder.onstop = async () => {
        // Stop browser speech recognition if running
        if (recognitionRef.current) {
          try {
            recognitionRef.current.stop();
          } catch (e) {}
          recognitionRef.current = null;
        }
        
        // Process any remaining chunks (Whisper mode only)
        if (transcriptionMode === 'whisper' && pendingChunksRef.current.length > 0 && hasApiKey) {
          const chunkBlob = new Blob(pendingChunksRef.current, { type: 'audio/webm' });
          const currentIndex = chunkIndexRef.current;
          setIsLazyTranscribing(true);
          await processChunkForTranscription(chunkBlob, currentIndex);
          setIsLazyTranscribing(false);
        }
        
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        
        // Use live transcript if available, otherwise will need full transcription
        // Append to previous transcript if this is a continuation
        if (liveTranscript) {
          const combinedTranscript = previousTranscriptRef.current 
            ? `${previousTranscriptRef.current}\n\n[Recording continued]\n\n${liveTranscript}`
            : liveTranscript;
          setTranscript(combinedTranscript);
          transcriptForSaveRef.current = combinedTranscript; // Keep ref for reliable save
        }
        
        // Auto-format if enabled (will use combined transcript)
        if (autoFormat && hasApiKey && liveTranscript) {
          const combinedTranscript = previousTranscriptRef.current 
            ? `${previousTranscriptRef.current}\n\n[Recording continued]\n\n${liveTranscript}`
            : liveTranscript;
          transcriptForSaveRef.current = combinedTranscript; // Ensure ref is set before formatting
          await autoProcessRecordingWithTranscript(combinedTranscript);
        } else if (autoFormat && hasApiKey) {
          await autoProcessRecording(blob);
        }
      };

      mediaRecorder.start(1000);
      setIsRecording(true);
      setIsPaused(false);
      setDuration(0);
      toast.success('Recording started' + (hasApiKey ? ' - Live transcription enabled' : ''));
    } catch (error) {
      console.error('Recording error:', error);
      toast.error('Failed to start recording. Please check microphone permissions.');
    }
  };

  // Auto-process with streaming format (when we already have transcript from lazy transcription)
  const autoProcessRecordingWithTranscript = async (transcriptText) => {
    setIsAutoProcessing(true);
    toast.info('Auto-processing: Formatting notes with streaming...');
    
    try {
      // Auto-generate title from transcript
      const words = transcriptText.split(' ').slice(0, 5).join(' ');
      if (!lectureTitle) {
        setLectureTitle(words + '...');
      }
      
      // Stream the formatted notes
      let streamedContent = '';
      setFormattedNotes('');
      setActiveView('notes'); // Show notes view while streaming
      
      await api.formatLectureStream(
        transcriptText,
        selectedModel,
        (chunk) => {
          // On each chunk, update the notes
          streamedContent += chunk;
          setFormattedNotes(streamedContent);
          // Convert to HTML for editor
          const htmlContent = streamedContent
            .split('\n')
            .map(line => `<p>${line || '<br>'}</p>`)
            .join('');
          setFormattedNotesHtml(htmlContent);
        },
        () => {
          // On complete
          toast.success('Auto-processing complete! Your notes are ready.');
          setActiveView('edit');
        },
        (error) => {
          console.error('Stream format error:', error);
          toast.error(`Formatting failed: ${error}`);
        }
      );
    } catch (error) {
      console.error('Auto-process error:', error);
      toast.error(`Auto-processing failed: ${error.message}`);
    } finally {
      setIsAutoProcessing(false);
    }
  };

  const autoProcessRecording = async (blob) => {
    setIsAutoProcessing(true);
    toast.info('Auto-processing: Transcribing audio...');
    
    try {
      // Step 1: Transcribe
      const transcribeResult = await api.transcribe(blob);
      setTranscript(transcribeResult.text);
      transcriptForSaveRef.current = transcribeResult.text; // Keep ref for reliable save
      setTotalCost(prev => prev + transcribeResult.cost);
      
      // Auto-generate title
      const words = transcribeResult.text.split(' ').slice(0, 5).join(' ');
      if (!lectureTitle) {
        setLectureTitle(words + '...');
      }
      
      toast.info('Auto-processing: Formatting notes with streaming...');
      
      // Step 2: Stream format for real-time display
      let streamedContent = '';
      setFormattedNotes('');
      setActiveView('notes'); // Show notes view while streaming
      
      await api.formatLectureStream(
        transcribeResult.text,
        selectedModel,
        (chunk) => {
          streamedContent += chunk;
          setFormattedNotes(streamedContent);
          const htmlContent = streamedContent
            .split('\n')
            .map(line => `<p>${line || '<br>'}</p>`)
            .join('');
          setFormattedNotesHtml(htmlContent);
        },
        () => {
          toast.success('Auto-processing complete! Your notes are ready.');
          setActiveView('edit');
        }
      );
    } catch (error) {
      console.error('Auto-process error:', error);
      toast.error(`Auto-processing failed: ${error.message}`);
      
      // Fallback to non-streaming if streaming fails
      try {
        const formatResult = await api.formatLecture(transcript, selectedModel);
        setFormattedNotes(formatResult.formatted_notes);
        const htmlContent = formatResult.formatted_notes
          .split('\n')
          .map(line => `<p>${line || '<br>'}</p>`)
          .join('');
        setFormattedNotesHtml(htmlContent);
        setTotalCost(prev => prev + formatResult.cost);
        toast.success('Auto-processing complete! Your notes are ready.');
        setActiveView('edit');
      } catch (fallbackError) {
        toast.error(`Formatting also failed: ${fallbackError.message}`);
      }
    } finally {
      setIsAutoProcessing(false);
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        // Resume browser speech recognition
        if (transcriptionMode === 'browser' && recognitionRef.current) {
          try {
            recognitionRef.current.start();
          } catch (e) {}
        }
        setIsPaused(false);
      } else {
        mediaRecorderRef.current.pause();
        // Pause browser speech recognition
        if (recognitionRef.current) {
          try {
            recognitionRef.current.stop();
          } catch (e) {}
        }
        setIsPaused(true);
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      // Stop browser speech recognition if running
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {}
        recognitionRef.current = null;
      }
      
      mediaRecorderRef.current.stop();
      streamRef.current?.getTracks().forEach(track => track.stop());
      setIsRecording(false);
      setIsPaused(false);
      
      if (autoFormat && hasApiKey) {
        toast.success('Recording stopped. Auto-processing will begin shortly...');
      } else {
        toast.success('Recording stopped');
      }
    }
  };

  const transcribeAudio = async () => {
    if (!audioBlob) {
      toast.error('No audio to transcribe');
      return;
    }

    if (!hasApiKey) {
      toast.error('Please add your OpenAI API key in settings');
      return;
    }

    setIsTranscribing(true);
    try {
      const result = await api.transcribe(audioBlob);
      setTranscript(result.text);
      transcriptForSaveRef.current = result.text; // Keep ref for reliable save
      setTotalCost(prev => prev + result.cost);
      toast.success('Transcription complete');
      setActiveView('transcript');
      
      if (!lectureTitle) {
        const words = result.text.split(' ').slice(0, 5).join(' ');
        setLectureTitle(words + '...');
      }
    } catch (error) {
      console.error('Transcription error:', error);
      toast.error(error.message || 'Transcription failed');
    } finally {
      setIsTranscribing(false);
    }
  };

  const formatTranscript = async () => {
    if (!transcript) {
      toast.error('No transcript to format');
      return;
    }

    if (!hasApiKey) {
      toast.error('Please add your OpenAI API key in settings');
      return;
    }

    setIsFormatting(true);
    setActiveView('notes'); // Switch to notes view to see streaming
    
    try {
      // Use streaming format for real-time display
      let streamedContent = '';
      setFormattedNotes('');
      
      await api.formatLectureStream(
        transcript,
        selectedModel,
        (chunk) => {
          streamedContent += chunk;
          setFormattedNotes(streamedContent);
          const htmlContent = streamedContent
            .split('\n')
            .map(line => `<p>${line || '<br>'}</p>`)
            .join('');
          setFormattedNotesHtml(htmlContent);
        },
        () => {
          toast.success('Notes formatted successfully');
          setActiveView('edit');
        }
      );
    } catch (error) {
      console.error('Formatting error:', error);
      // Fallback to non-streaming
      try {
        const result = await api.formatLecture(transcript, selectedModel);
        setFormattedNotes(result.formatted_notes);
        const htmlContent = result.formatted_notes
          .split('\n')
          .map(line => `<p>${line || '<br>'}</p>`)
          .join('');
        setFormattedNotesHtml(htmlContent);
        setTotalCost(prev => prev + result.cost);
        toast.success('Notes formatted successfully');
        setActiveView('edit');
      } catch (fallbackError) {
        toast.error(fallbackError.message || 'Formatting failed');
      }
    } finally {
      setIsFormatting(false);
    }
  };

  const generateSuggestions = async () => {
    if (!currentLecture?.id) {
      toast.error('Please save the lecture first');
      return;
    }

    setIsGeneratingSuggestions(true);
    try {
      const result = await api.generateSuggestions(currentLecture.id);
      setAiSuggestions(result.suggestions);
      toast.success(`Generated ${result.count} study suggestions`);
    } catch (error) {
      console.error('Suggestions error:', error);
      toast.error(error.message || 'Failed to generate suggestions');
    } finally {
      setIsGeneratingSuggestions(false);
    }
  };

  const saveLecture = async () => {
    // Use ref as fallback for transcript (handles React state timing issues on mobile)
    const transcriptToSave = transcript || transcriptForSaveRef.current;
    
    if (!transcriptToSave && !formattedNotes) {
      toast.error('Nothing to save');
      return;
    }

    setIsSaving(true);
    try {
      if (currentLecture) {
        // Update existing - include raw_transcript if we have new transcript content
        const updateData = {
          title: lectureTitle || 'Untitled Lecture',
          formatted_notes: formattedNotes,
          formatted_notes_html: formattedNotesHtml,
          ai_suggestions: aiSuggestions,
          subject_id: subjectId,
          status: formattedNotes ? 'completed' : 'processing'
        };
        
        // Include raw_transcript if we have transcript content that differs from existing
        if (transcriptToSave && transcriptToSave !== currentLecture.raw_transcript) {
          updateData.raw_transcript = transcriptToSave;
        }
        
        const updated = await api.updateLecture(currentLecture.id, updateData);
        setCurrentLecture(updated);
        toast.success('Lecture updated');
      } else {
        // Create new
        const lecture = await api.createLecture({
          title: lectureTitle || 'Untitled Lecture',
          raw_transcript: transcriptToSave,
          audio_duration: duration,
          subject_id: subjectId
        });
        
        // Update with formatted content
        if (formattedNotes || formattedNotesHtml) {
          const updated = await api.updateLecture(lecture.id, {
            formatted_notes: formattedNotes,
            formatted_notes_html: formattedNotesHtml,
            status: 'completed'
          });
          setCurrentLecture(updated);
        } else {
          setCurrentLecture(lecture);
        }
        
        toast.success('Lecture saved');
        navigate(`/lecture/${lecture.id}`, { replace: true });
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.message || 'Failed to save lecture');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditorChange = (html) => {
    setFormattedNotesHtml(html);
    // Also update plain text version for backend compatibility
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    setFormattedNotes(tempDiv.textContent || tempDiv.innerText || '');
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const hasApiKey = isLlmInferenceAvailable(user);
  
  // Check if browser speech recognition is supported
  const browserSpeechSupported = typeof window !== 'undefined' && 
    ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window);

  // Setup Web Speech API for free transcription
  const setupBrowserSpeechRecognition = () => {
    if (!browserSpeechSupported) return null;
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    
    let finalTranscript = '';
    
    recognition.onresult = (event) => {
      let interimTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
          // Update live transcript with final results
          setLiveTranscript(prev => {
            const newTranscript = prev ? prev + ' ' + transcript : transcript;
            return newTranscript.trim();
          });
        } else {
          interimTranscript += transcript;
        }
      }
    };
    
    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'no-speech') {
        // Restart recognition if no speech detected
        try {
          recognition.stop();
          setTimeout(() => {
            if (isRecording) recognition.start();
          }, 100);
        } catch (e) {}
      }
    };
    
    recognition.onend = () => {
      // Auto-restart if still recording (browser speech recognition stops after silence)
      if (isRecording && !isPaused && transcriptionMode === 'browser') {
        try {
          recognition.start();
        } catch (e) {
          console.error('Failed to restart recognition:', e);
        }
      }
    };
    
    return recognition;
  };
  const isProcessing = isTranscribing || isFormatting || isAutoProcessing;

  return (
    <div className="min-h-screen bg-background noise-bg">
      {/* Header */}
      <header className="glass sticky top-0 z-50">
        <div className="mx-auto flex h-auto min-h-16 max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-3 sm:h-16 sm:min-h-0 sm:flex-nowrap sm:px-6 sm:py-0">
          <div className="flex min-w-0 items-center gap-3 sm:gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon">
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </Link>
            <span className="font-serif text-xl text-foreground">
              {id ? 'Edit Lecture' : 'New Lecture'}
            </span>
          </div>
          <div className="flex w-full flex-wrap items-center gap-3 sm:w-auto">
            {totalCost > 0 && (
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <DollarSign className="w-4 h-4" />
                {totalCost.toFixed(4)}
              </span>
            )}
            <Button
              onClick={saveLecture}
              disabled={isSaving || (!transcript && !formattedNotes)}
              className="rounded-xl"
              data-testid="save-lecture-btn"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content - Split View */}
      <main className="flex min-h-[calc(100dvh-4rem)] flex-col lg:h-[calc(100vh-4rem)] lg:flex-row">
        {/* Left Panel - Recording Controls */}
        <div className="flex w-full flex-col overflow-y-auto border-b border-border bg-card/50 p-4 sm:p-6 lg:w-80 lg:border-b-0 lg:border-r">
          {/* Auto-Format Info Banner */}
          {showAutoFormatInfo && autoFormat && hasApiKey && !id && (
            <div className="mb-4 p-3 bg-primary/10 rounded-xl flex items-start gap-2 relative">
              <Zap className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Auto-format is ON</p>
                <p className="text-xs text-muted-foreground">
                  Notes will be automatically transcribed and formatted when you end recording.
                </p>
              </div>
              <button
                onClick={() => setShowAutoFormatInfo(false)}
                className="text-muted-foreground hover:text-foreground text-xs"
              >
                ×
              </button>
            </div>
          )}

          {/* Transcription Mode Selector */}
          <div className="mb-4">
            <Tabs value={transcriptionMode} onValueChange={setTranscriptionMode} className="w-full">
              <TabsList className="w-full h-9 grid grid-cols-2">
                <TabsTrigger 
                  value="browser" 
                  className="text-xs"
                  disabled={isRecording || !browserSpeechSupported}
                >
                  <span className="flex items-center gap-1">
                    🎤 Browser
                    <span className="text-[10px] bg-green-500/20 text-green-600 px-1 rounded">FREE</span>
                  </span>
                </TabsTrigger>
                <TabsTrigger 
                  value="whisper" 
                  className="text-xs"
                  disabled={isRecording || !hasApiKey}
                >
                  <span className="flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Whisper
                    <span className="text-[10px] bg-amber-500/20 text-amber-600 px-1 rounded">$0.006/min</span>
                  </span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <p className="text-[10px] text-muted-foreground mt-1 text-center">
              {!browserSpeechSupported && transcriptionMode === 'browser'
                ? "⚠️ Browser speech not supported - please use Whisper or try Chrome"
                : transcriptionMode === 'browser' 
                  ? "Using browser's built-in speech recognition (works offline, good accuracy)"
                  : "Using OpenAI Whisper (best accuracy, requires API key)"}
            </p>
          </div>

          {/* Subject Selector - Yellow Dot */}
          <div className="mb-4">
            <SubjectSelector
              currentSubjectId={subjectId}
              onSelect={setSubjectId}
              showTriggerDot={true}
            />
          </div>

          {/* Title Input */}
          <div className="mb-4">
            <Input
              placeholder="Lecture title..."
              value={lectureTitle}
              onChange={(e) => setLectureTitle(e.target.value)}
              className="h-11 text-lg font-medium"
              data-testid="lecture-title-input"
            />
          </div>

          {/* Recording Section */}
          <Card className="border-border/50 shadow-card mb-4">
            <CardContent className="pt-6">
              {/* Previous transcript indicator */}
              {!isRecording && hasPreviousTranscript && (
                <div className="flex items-center justify-between mb-4 p-2 bg-primary/10 rounded-lg">
                  <span className="text-xs text-primary flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    Recording will append to existing transcript
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs text-muted-foreground hover:text-destructive"
                    onClick={() => {
                      previousTranscriptRef.current = '';
                      setHasPreviousTranscript(false);
                      setTranscript('');
                      setFormattedNotes('');
                      setFormattedNotesHtml('');
                      setLiveKeyPoints('');
                      toast.info('Starting fresh - previous transcript cleared');
                    }}
                  >
                    Start Fresh
                  </Button>
                </div>
              )}
              
              <div className="flex flex-col items-center">
                <button
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isProcessing || !hasApiKey}
                  className={`w-24 h-24 rounded-full flex items-center justify-center transition-all ${
                    isRecording
                      ? 'bg-destructive recording-active'
                      : isProcessing
                      ? 'bg-primary/50 cursor-wait'
                      : hasApiKey
                      ? 'bg-primary hover:scale-105 active:scale-95'
                      : 'bg-muted cursor-not-allowed'
                  }`}
                  data-testid="record-btn"
                >
                  {isProcessing ? (
                    <Loader2 className="w-10 h-10 text-primary-foreground animate-spin" />
                  ) : isRecording ? (
                    <Square className="w-10 h-10 text-white" />
                  ) : (
                    <Mic className="w-10 h-10 text-primary-foreground" />
                  )}
                </button>

                {isAutoProcessing && (
                  <div className="mt-3 text-sm text-primary font-medium animate-pulse">
                    Processing...
                  </div>
                )}

                <div className="mt-3 flex items-center gap-2 text-xl font-mono">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  {formatTime(duration)}
                </div>

                {isRecording && (
                  <div className="mt-3 flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={pauseRecording}
                      className="rounded-xl h-8"
                    >
                      {isPaused ? <Play className="w-3 h-3 mr-1" /> : <Pause className="w-3 h-3 mr-1" />}
                      {isPaused ? 'Resume' : 'Pause'}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={stopRecording}
                      className="rounded-xl h-8"
                      data-testid="stop-btn"
                    >
                      <Square className="w-3 h-3 mr-1" />
                      End
                    </Button>
                  </div>
                )}

                {isRecording && !isPaused && (
                  <div className="mt-4 flex items-end justify-center gap-0.5 h-8">
                    {[...Array(15)].map((_, i) => (
                      <div
                        key={i}
                        className="w-1 bg-primary rounded-full waveform-bar"
                        style={{ animationDelay: `${i * 0.1}s`, height: '20%' }}
                      />
                    ))}
                  </div>
                )}

                {/* Live Key Points Display */}
                {isRecording && liveKeyPoints && (
                  <div className="mt-4 w-full">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-3 h-3 text-amber-500" />
                      <span className="text-xs font-medium text-amber-500">Live Key Points</span>
                      {isGeneratingKeyPoints && (
                        <Loader2 className="w-3 h-3 text-amber-500 animate-spin" />
                      )}
                    </div>
                    <div className="max-h-24 overflow-y-auto p-2 bg-amber-500/10 rounded-lg text-xs text-foreground whitespace-pre-wrap">
                      {liveKeyPoints}
                    </div>
                  </div>
                )}

                {/* Live Transcript Display */}
                {isRecording && liveTranscript && (
                  <div className="mt-3 w-full">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-3 h-3 text-muted-foreground" />
                      <span className="text-xs font-medium text-muted-foreground">Raw Transcript</span>
                      {isLazyTranscribing && (
                        <Loader2 className="w-3 h-3 text-muted-foreground animate-spin" />
                      )}
                    </div>
                    <div className="max-h-20 overflow-y-auto p-2 bg-muted/50 rounded-lg text-xs text-muted-foreground">
                      {liveTranscript}
                    </div>
                  </div>
                )}
              </div>

              {!hasApiKey && (
                <div className="mt-4 p-2 bg-amber-500/10 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-600">
                    Add your OpenAI API key in{' '}
                    <Link to="/settings" className="underline">settings</Link>.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Model Selection */}
          <div className="mb-4">
            <label className="text-sm text-muted-foreground mb-2 block">AI Model</label>
            <Select value={selectedModel} onValueChange={setSelectedModel}>
              <SelectTrigger className="h-10" data-testid="model-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODELS.map((model) => (
                  <SelectItem key={model.value} value={model.value}>
                    {model.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Action Buttons */}
          {audioBlob && !isRecording && !autoFormat && (
            <Button
              onClick={transcribeAudio}
              disabled={isTranscribing}
              className="w-full mb-3 rounded-xl"
              data-testid="transcribe-btn"
            >
              {isTranscribing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
              Transcribe
            </Button>
          )}

          {transcript && (
            <Button
              onClick={formatTranscript}
              disabled={isFormatting}
              variant="secondary"
              className="w-full mb-3 rounded-xl"
              data-testid="format-btn"
            >
              {isFormatting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
              {formattedNotes ? 'Re-format' : 'Format Notes'}
            </Button>
          )}

          {currentLecture && formattedNotes && (
            <Button
              onClick={generateSuggestions}
              disabled={isGeneratingSuggestions}
              variant="outline"
              className="w-full rounded-xl"
              data-testid="suggestions-btn"
            >
              {isGeneratingSuggestions ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Generate AI Suggestions
            </Button>
          )}

          {/* I'm Confused Button */}
          {currentLecture && formattedNotes && hasApiKey && (
            <Button
              onClick={() => setShowConfusedModal(true)}
              variant="secondary"
              className="w-full rounded-xl mt-3 bg-amber-500/10 hover:bg-amber-500/20 text-amber-700 border-amber-500/30"
              data-testid="confused-btn"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              I'm Confused
            </Button>
          )}
        </div>

        {/* Right Panel - Content View */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* View Tabs */}
          {(transcript || formattedNotes) && (
            <div className="border-b border-border px-6 pt-4">
              <Tabs value={activeView} onValueChange={setActiveView}>
                <TabsList>
                  {transcript && (
                    <TabsTrigger value="transcript" className="gap-2">
                      <FileText className="w-4 h-4" />
                      Transcript
                    </TabsTrigger>
                  )}
                  {formattedNotes && (
                    <>
                      <TabsTrigger value="notes" className="gap-2">
                        <Eye className="w-4 h-4" />
                        View Notes
                      </TabsTrigger>
                      <TabsTrigger value="edit" className="gap-2">
                        <Edit3 className="w-4 h-4" />
                        Edit & Highlight
                      </TabsTrigger>
                    </>
                  )}
                </TabsList>
              </Tabs>
            </div>
          )}

          {/* Content Area */}
          <ScrollArea className="flex-1">
            <div className="p-6">
              {activeView === 'edit' && formattedNotes ? (
                <div className="max-w-4xl mx-auto border border-border rounded-lg overflow-hidden">
                  <RichTextEditor
                    content={formattedNotesHtml || formattedNotes.split('\n').map(line => `<p>${line || '<br>'}</p>`).join('')}
                    onChange={handleEditorChange}
                    suggestions={aiSuggestions}
                    onApplySuggestion={(s, i) => console.log('Applied suggestion:', s)}
                    placeholder="Your formatted notes will appear here..."
                  />
                </div>
              ) : activeView === 'notes' && formattedNotes ? (
                <div className="max-w-3xl mx-auto prose-lecture" data-testid="formatted-notes">
                  <div className="whitespace-pre-wrap">{formattedNotes}</div>
                </div>
              ) : activeView === 'transcript' && transcript ? (
                <div className="max-w-3xl mx-auto" data-testid="transcript-view">
                  <h2 className="font-serif text-2xl mb-6">Raw Transcript</h2>
                  <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                    {transcript}
                  </p>
                </div>
              ) : isAutoProcessing ? (
                <div className="h-full flex items-center justify-center text-center py-20">
                  <div>
                    <Loader2 className="w-16 h-16 text-primary mx-auto mb-4 animate-spin" />
                    <h3 className="font-serif text-xl text-foreground mb-2">
                      Processing Your Lecture
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Transcribing and formatting notes...
                    </p>
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-center py-20">
                  <div>
                    <Mic className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                    <h3 className="font-serif text-xl text-muted-foreground mb-2">
                      Ready to Record
                    </h3>
                    <p className="text-muted-foreground/70 text-sm max-w-sm mx-auto">
                      Click the record button to start. 
                      {autoFormat && hasApiKey && ' Notes will be formatted automatically.'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </main>

      {/* Confused Modal */}
      <ConfusedModal
        isOpen={showConfusedModal}
        onClose={() => setShowConfusedModal(false)}
        lectureContent={formattedNotes}
        lectureId={currentLecture?.id}
        subjectId={subjectId}
        defaultMode={user?.realtime_mode || 'text'}
        autoUpdateNotes={user?.auto_update_notes || false}
        onNoteSuggestion={(suggestion) => {
          // Append suggestion to notes
          const updatedNotes = formattedNotes + '\n\n--- AI Clarification ---\n' + suggestion;
          setFormattedNotes(updatedNotes);
          // Also update HTML version
          const htmlSuggestion = `<p><br></p><p><strong>--- AI Clarification ---</strong></p><p>${suggestion}</p>`;
          setFormattedNotesHtml(prev => prev + htmlSuggestion);
        }}
      />
    </div>
  );
}
